import { useState, useEffect, useCallback, useRef, createContext, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Shield } from "lucide-react";
import { FloatingParticles } from "./components/FloatingParticles";
import { NewsletterCapture } from "./components/NewsletterCapture";
import { ReturnVisitorWelcome } from "./components/ReturnVisitorWelcome";
import { RequireMembership, ORACLE_TIERS } from "./components/RequireMembership";
import { SanctuaryLoading } from "./components/SanctuaryLoading";
import { RouteFlashTransition } from "./components/RouteFlashTransition";
import { AmbientDust } from "./components/AmbientDust";
import { AmbientSoundToggle } from "./components/AmbientSoundToggle";
import { ChunkLoadErrorBoundary } from "./components/ChunkLoadErrorBoundary";
import { logError } from "./lib/logger";

// Page-level code splitting — bundles load only when visitors hit a route.
// Each page is fetched lazily; <Suspense> shows the SanctuaryLoading fallback.
const PublicLanding         = lazy(() => import("./pages/PublicLanding"));
const LotusGatePage         = lazy(() => import("./pages/LotusGate"));
const ProtectedHub          = lazy(() => import("./pages/ProtectedHub"));
const VerifyEmailPage       = lazy(() => import("./pages/VerifyEmail"));
const ResetPasswordPage     = lazy(() => import("./pages/ResetPassword"));
const AuthCallback          = lazy(() => import("./pages/AuthCallback"));
const AdminDashboard        = lazy(() => import("./pages/AdminDashboard"));

// Named-export modules wrapped via dynamic-import shims (default-export expected by lazy).
const PricingPage           = lazy(() => import("./Pricing").then(m => ({ default: m.PricingPage })));
const PaymentSuccessPage    = lazy(() => import("./Pricing").then(m => ({ default: m.PaymentSuccessPage })));
const MembershipPage        = lazy(() => import("./Pricing").then(m => ({ default: m.MembershipPage })));
const DreamJournalPage      = lazy(() => import("./Portal").then(m => ({ default: m.DreamJournalPage })));
const OracleBookingPage     = lazy(() => import("./Portal").then(m => ({ default: m.OracleBookingPage })));
const AdminInboxPage        = lazy(() => import("./Portal").then(m => ({ default: m.AdminInboxPage })));
const TransmissionsPage     = lazy(() => import("./Transmissions").then(m => ({ default: m.TransmissionsPage })));
const AdminTransmissionsPage = lazy(() => import("./Transmissions").then(m => ({ default: m.AdminTransmissionsPage })));
const AdminPricingPage      = lazy(() => import("./AdminPanels").then(m => ({ default: m.AdminPricingPage })));
const AdminContentPage      = lazy(() => import("./AdminPanels").then(m => ({ default: m.AdminContentPage })));
const AdminSubscribersPage  = lazy(() => import("./AdminPanels").then(m => ({ default: m.AdminSubscribersPage })));
const AdminPaymentsPage     = lazy(() => import("./AdminPayments").then(m => ({ default: m.AdminPaymentsPage })));
const AdminBusinessPage     = lazy(() => import("./AdminBusiness").then(m => ({ default: m.AdminBusinessPage })));
const BlogPage              = lazy(() => import("./Sanctuary").then(m => ({ default: m.BlogPage })));
const ShopPage              = lazy(() => import("./Sanctuary").then(m => ({ default: m.ShopPage })));
const MusicPage             = lazy(() => import("./Sanctuary").then(m => ({ default: m.MusicPage })));
const SoulCodexPage         = lazy(() => import("./SoulCodex").then(m => ({ default: m.SoulCodexPage })));
const EnergyHealingPage     = lazy(() => import("./EnergyHealing").then(m => ({ default: m.EnergyHealingPage })));
const HealingFrequenciesPage = lazy(() => import("./HealingFrequencies").then(m => ({ default: m.HealingFrequenciesPage })));
const DreamWalksPage         = lazy(() => import("./DreamWalks").then(m => ({ default: m.DreamWalksPage })));

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// AUTH CONTEXT
// ============================================

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: Skip auth check if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      logError('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================
// CONTENT PROTECTION
// ============================================

// ContentProtection now lives in ./pages/ProtectedHub.jsx (only used by the hub).

// ============================================
// PAGE VIEW TRACKER
// ============================================

export const usePageTracker = (page) => {
  const { user } = useAuth();

  useEffect(() => {
    if (user && page) {
      fetch(`${API_URL}/api/track-page-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ page })
      }).catch(() => {});
    }
  }, [user, page]);
};

// ============================================
// FLOATING PARTICLES
// ============================================

// FloatingParticles, NewsletterCapture, ReturnVisitorWelcome, RequireSubscriber
// are imported from ./components/* (extracted in the App.js refactor).

// ============================================
// SCROLL REVEAL HOOK
// ============================================

export const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return [ref, isRevealed];
};


// VerifyEmailPage extracted to ./pages/VerifyEmail.jsx


// AuthCallback extracted to ./pages/AuthCallback.jsx

// PublicLanding extracted to ./pages/PublicLanding.jsx

// ============================================
// LotusGatePage extracted to ./pages/LotusGate.jsx
// ProtectedHub + MainHubContent + Hero/Oracle/Portal/Path/Codex/Transmission/SacredCreations sections extracted to ./pages/ProtectedHub.jsx


// AdminDashboard extracted to ./pages/AdminDashboard.jsx

// ============================================
// APP ROUTER
// ============================================

function AppRouter() {
  const location = useLocation();

  // Capture ?ref= referral codes anywhere on the site
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref) localStorage.setItem('soul-ref', ref);
  }, [location.search]);

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return (
      <ChunkLoadErrorBoundary>
        <Suspense fallback={<SanctuaryLoading />}>
          <AuthCallback />
        </Suspense>
      </ChunkLoadErrorBoundary>
    );
  }

  return (
    <ChunkLoadErrorBoundary>
      <Suspense fallback={<SanctuaryLoading />}>
        <RouteFlashTransition />
        <AmbientDust />
        <Routes>
        <Route path="/" element={<PublicLanding />} />
        <Route path="/lotus-gate" element={<LotusGatePage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/membership" element={<MembershipPage />} />
        <Route path="/dream-journal" element={<RequireMembership sanctuaryName="Dream Journal" recommendedPackage="mystic"><DreamJournalPage /></RequireMembership>} />
        <Route path="/oracle" element={<RequireMembership sanctuaryName="Lotus Eye Oracle" recommendedPackage="mystic" requiredTiers={ORACLE_TIERS}><OracleBookingPage /></RequireMembership>} />
        <Route path="/transmissions" element={<RequireMembership sanctuaryName="Daily Transmissions" recommendedPackage="mystic"><TransmissionsPage /></RequireMembership>} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/music" element={<MusicPage />} />
        <Route path="/codex" element={<RequireMembership sanctuaryName="Soul Codex" recommendedPackage="seeker"><SoulCodexPage /></RequireMembership>} />
        <Route path="/energy-healing" element={<RequireMembership sanctuaryName="Energy Healing Services" recommendedPackage="mystic"><EnergyHealingPage /></RequireMembership>} />
        <Route path="/healing-frequencies" element={<RequireMembership sanctuaryName="Healing Frequencies" recommendedPackage="mystic"><HealingFrequenciesPage /></RequireMembership>} />
        <Route path="/dream-walks" element={<RequireMembership sanctuaryName="Dream Walks" recommendedPackage="seeker"><DreamWalksPage /></RequireMembership>} />
        <Route path="/admin/inbox" element={<AdminInboxPage />} />
        <Route path="/admin/transmissions" element={<AdminTransmissionsPage />} />
        <Route path="/admin/pricing" element={<AdminPricingPage />} />
        <Route path="/admin/content" element={<AdminContentPage />} />
        <Route path="/admin/newsletter" element={<AdminSubscribersPage />} />
        <Route path="/admin/payments" element={<AdminPaymentsPage />} />
        <Route path="/admin/business" element={<AdminBusinessPage />} />
        <Route path="/hub" element={<RequireMembership sanctuaryName="Soul Sanctuary"><ProtectedHub /></RequireMembership>} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </ChunkLoadErrorBoundary>
  );
}

// Floating Owner Dashboard FAB — visible only to admins on every page except /admin itself
const OwnerDashboardFab = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  if (!user?.is_admin) return null;
  // Hide on the admin dashboard itself to avoid duplication
  if (location.pathname.startsWith("/admin")) return null;
  return (
    <button
      data-testid="owner-dashboard-fab"
      onClick={() => navigate("/admin")}
      className="fixed bottom-6 left-6 z-[90] px-5 py-3 rounded-full reveal-button text-[var(--text-accent)] text-[11px] uppercase tracking-[0.25em] font-medium inline-flex items-center gap-2 shadow-[0_0_30px_rgba(232,213,163,0.35)]"
      title="Owner Dashboard"
    >
      <Shield size={14} />
      Owner Dashboard
    </button>
  );
};

// Wrapper that renders router + FAB
const AppShell = () => (
  <>
    <AppRouter />
    <OwnerDashboardFab />
    <AmbientSoundToggle />
  </>
);

// ============================================
// MAIN APP
// ============================================

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
